import React, { useState, useEffect } from 'react';
import { useOrg } from '../../hooks/useOrg';
import { useOrgUsers } from '../../hooks/useOrgUsers';
import { useSurfaceTypes } from '../../hooks/useSurfaceTypes';
import { atpRecordService } from '../../services/atpRecordService';
import { departmentService, roomService } from '../../services/firebaseService';
import { useNotifications } from '../NotificationSystem';
import authService from '../../services/authService';
import './ATPRecordForm.css';

/** Fără emoji în titlu — unele fonturi / CSS globale taie textul în interiorul <label> */
const INTERPRETATIONS = [
  { value: 'conform', title: 'Conform', subtitle: 'Rezultat în limite acceptate' },
  { value: 'neconform', title: 'Neconform', subtitle: 'Depășire / pozitiv' },
  { value: 'echivoc', title: 'Echivoc', subtitle: 'Necesită clarificare' },
  { value: 'na', title: 'N/A', subtitle: 'Nu se aplică' }
];

const ATPRecordForm = ({ onSaved, workflowExecutionId = null, workflowNodeId = null }) => {
  const { activeOrgId } = useOrg();
  const { notify } = useNotifications();
  const { users: orgUsers } = useOrgUsers();
  const { surfaceTypes } = useSurfaceTypes();

  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAtpId, setSavedAtpId] = useState(null);

  const currentUser = authService.getCurrentUser();

  const [form, setForm] = useState({
    department_id: '',
    department_name: '',
    room_id: '',
    room_name: '',
    surface_type_id: '',
    surface_type_name: '',
    operator_id: currentUser?.uid || '',
    operator_name: '',
    value: '',
    description: '',
    interpretation: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!activeOrgId) return;
    departmentService.getAll().then(setDepartments).catch(console.warn);
  }, [activeOrgId]);

  useEffect(() => {
    if (!form.department_id) {
      setRooms([]);
      return;
    }
    roomService.getAll().then((all) => {
      setRooms(all.filter((r) => r.departmentId === form.department_id));
    }).catch(console.warn);
  }, [form.department_id]);

  useEffect(() => {
    if (form.operator_id && orgUsers.length > 0) {
      const u = orgUsers.find((x) => x.userId === form.operator_id);
      if (u) setForm((prev) => ({ ...prev, operator_name: u.fullName }));
    }
  }, [form.operator_id, orgUsers]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  };

  const validate = () => {
    const e = {};
    if (!form.department_id) e.department_id = 'Departamentul este obligatoriu';
    if (!form.room_id) e.room_id = 'Camera/salonul este obligatoriu';
    if (!form.surface_type_id) e.surface_type_id = 'Suprafața este obligatorie';
    if (!form.interpretation) e.interpretation = 'Interpretarea este obligatorie';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!activeOrgId) return;
    setSaving(true);
    try {
      const result = await atpRecordService.createATPRecord(activeOrgId, {
        ...form,
        workflow_execution_id: workflowExecutionId,
        workflow_node_id: workflowNodeId
      }, currentUser?.uid);

      setSavedAtpId(result.atp_id);
      notify.success(`Înregistrare ATP nr. ${result.atp_id} salvată`);
      if (onSaved) onSaved(result);

      setForm({
        department_id: '',
        department_name: '',
        room_id: '',
        room_name: '',
        surface_type_id: '',
        surface_type_name: '',
        operator_id: currentUser?.uid || '',
        operator_name: '',
        value: '',
        description: '',
        interpretation: ''
      });
    } catch (err) {
      notify.error('Eroare la salvare: ' + (err.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="atp-record-shell">
      <div className="atp-record-card">
        <header className="atp-record-header">
          <h3 className="atp-record-title">Înregistrare test ATP</h3>
          <p className="atp-record-id-line">
            Număr ATP (<code>atp_id</code>):{' '}
            {savedAtpId != null ? (
              <strong className="atp-id-emphasis">#{savedAtpId}</strong>
            ) : (
              <span className="atp-id-pending">se atribuie automat la salvare</span>
            )}
          </p>
          <p className="atp-record-lead">
            <strong>request_id</strong> este doar pentru <em>cereri de laborator</em> (formularul „Cerere Lab”), nu pentru ATP.
          </p>
        </header>

        {savedAtpId != null && (
          <div className="atp-saved-banner" role="status">
            <span className="atp-saved-text">
              Ultima înregistrare salvată — număr ATP: <strong className="atp-id-emphasis">#{savedAtpId}</strong>
            </span>
            <button
              type="button"
              className="atp-saved-dismiss"
              onClick={() => setSavedAtpId(null)}
              aria-label="Ascunde"
            >
              ×
            </button>
          </div>
        )}

        <div className="atp-record-form">
          <div className="sampling-form-field">
            <span className="field-label">Departament <span className="required">*</span></span>
            <select
              value={form.department_id}
              onChange={(e) => {
                const dept = departments.find((d) => d.id === e.target.value);
                handleChange('department_id', e.target.value);
                handleChange('department_name', dept?.name || '');
                handleChange('room_id', '');
                handleChange('room_name', '');
              }}
            >
              <option value="">Selectează departament...</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {errors.department_id && <span className="error">{errors.department_id}</span>}
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Salon / Cameră <span className="required">*</span></span>
            <select
              value={form.room_id}
              onChange={(e) => {
                const room = rooms.find((r) => r.id === e.target.value);
                handleChange('room_id', e.target.value);
                handleChange('room_name', room?.name || '');
              }}
              disabled={!form.department_id}
            >
              <option value="">Selectează salon...</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {errors.room_id && <span className="error">{errors.room_id}</span>}
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Suprafață <span className="required">*</span></span>
            <select
              value={form.surface_type_id}
              onChange={(e) => {
                const st = surfaceTypes.find((s) => s.id === e.target.value);
                handleChange('surface_type_id', e.target.value);
                handleChange('surface_type_name', st?.name || '');
              }}
            >
              <option value="">Selectează suprafață...</option>
              {surfaceTypes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {errors.surface_type_id && <span className="error">{errors.surface_type_id}</span>}
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Operator</span>
            <select
              value={form.operator_id}
              onChange={(e) => handleChange('operator_id', e.target.value)}
            >
              <option value="">Selectează operator...</option>
              {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.fullName}</option>)}
            </select>
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Valoare ATP (RLU)</span>
            <input
              type="number"
              min={0}
              value={form.value}
              onChange={(e) => handleChange('value', e.target.value)}
              placeholder="ex: 250"
            />
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Descriere / Observații</span>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Observații suplimentare..."
            />
          </div>

          <div className="sampling-form-field sampling-form-field--interpret">
            <span className="field-label">Interpretare <span className="required">*</span></span>
            <div className="atp-interpret-grid" role="group" aria-label="Interpretare rezultat ATP">
              {INTERPRETATIONS.map((opt) => {
                const selected = form.interpretation === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`${opt.title}: ${opt.subtitle}`}
                    className={`atp-interpret-btn ${selected ? 'atp-interpret-btn--selected' : ''}`}
                    onClick={() => handleChange('interpretation', opt.value)}
                  >
                    <span className="atp-interpret-btn__title">{opt.title}</span>
                    <span className="atp-interpret-btn__sub">{opt.subtitle}</span>
                  </button>
                );
              })}
            </div>
            {errors.interpretation && <span className="error">{errors.interpretation}</span>}
          </div>

          <div className="atp-form-actions">
            <button
              type="button"
              className="btn btn-primary atp-submit-btn"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Se salvează...' : 'Salvează înregistrare ATP'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ATPRecordForm;
